import React, { useCallback, useEffect, useState } from "react";
import { SafeAreaProvider } from "react-native-safe-area-context";
import NetInfo from "@react-native-community/netinfo";
import { BlurView } from "@react-native-community/blur";
import {
  ActivityIndicator,
  AppState,
  AppStateStatus,
  BackHandler,
  Linking,
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
} from "react-native";

import AppNavigator from "./src/components/navigation/AppNavigator";
import { AuthProvider } from "./src/context/AuthContext";
import { AppLoader } from "./src/components/shared/utils/AppLoader";
import { ToastHost } from "./src/components/shared/ui/ToastHost";

import NoInternetScreen from "./src/components/shared/ui/NoInternetScreen";
import { Storage } from "./src/components/services/storage/storage";
import { StorageKeys } from "./src/components/services/storage/keys";
import { AppVersionService } from "./src/components/services/AppVersionService";
import { UserDeviceService } from "./src/components/services/UserDeviceService";
import { UserRegistrationService } from "./src/components/services/UserRegistrationService";
import { environment } from "./src/components/environment";

type UpdateState = {
  visible: boolean;
  version: string;
  url: string;
};

type ErrorState = {
  visible: boolean;
  title: string;
  description: string;
};

const normalizePlatformName = (value: unknown) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z]/g, "");

export default function App() {

  const [isConnected, setIsConnected] = useState<boolean | null>(null);
  const [isVersionCheckComplete, setIsVersionCheckComplete] = useState(false);
  const [updateState, setUpdateState] = useState<UpdateState>({
    visible: false,
    version: "",
    url: "",
  });
  const [errorState, setErrorState] = useState<ErrorState>({
    visible: false,
    title: "",
    description: "",
  });

  const showVersionCheckError = (description: string) => {
    setErrorState({
      visible: true,
      title: "Version Check Failed",
      description,
    });
  };

  const checkInternet = async () => {
    const state = await NetInfo.fetch();
    setIsConnected(state.isConnected);
  };

  const checkForAppUpdate = useCallback(async () => {
    try {
      const currentVersion = environment.appVersion?.trim();
      if (currentVersion) {
        await Storage.set(StorageKeys.appVersion, currentVersion);
      }

      if (Platform.OS === "web") {
        return;
      }

      const enumerationResponse = await UserRegistrationService.getEnumeration("AppPlatform");
      const platformItems = Array.isArray(enumerationResponse?.data)
        ? enumerationResponse.data
        : [];
      const currentPlatform = normalizePlatformName(Platform.OS);

      const platformEntry = platformItems.find((item: any) => {
        const rawValue = normalizePlatformName(
          item?.value ??
          item?.name ??
          item?.code ??
          item?.displayName ??
          ""
        );

        return (
          rawValue === currentPlatform ||
          (currentPlatform === "ios" && ["iphone", "ipad", "ios"].includes(rawValue)) ||
          (currentPlatform === "android" && ["android", "googleplay"].includes(rawValue))
        );
      });

      const platformId = String(platformEntry?.enumerationId ?? platformEntry?.id ?? "");
      if (!platformId) {
        showVersionCheckError("Platform configuration was not found. Please retry.");
        return;
      }

      const response = await AppVersionService.getApiVersion({
        platformId: platformId,
        ...(currentVersion ? { currentVersion } : {}),
      });

      if (Number(response?.status) !== 200) {
        showVersionCheckError(response?.message?.trim() || "Unable to check app version right now.");
        return;
      }
      const config = response?.data;

      if (!config?.latestVersion) {
        showVersionCheckError("App version details were not returned. Please retry.");
        return;
      }

      if (config.isUpdateAvailable) {
        const fallbackRedirectUrl = String(platformEntry?.parameter ?? "").trim();

        setUpdateState({
          visible: true,
          version: config.latestVersion,
          url: config.redirectUrl?.trim() || fallbackRedirectUrl,
        });
        return;
      }
    } catch (error: any) {
      showVersionCheckError(
        error?.message?.trim() || "Something went wrong while checking for app updates."
      );
    } finally {
      setIsVersionCheckComplete(true);
    }
  }, []);

  const handleUpdatePress = async () => {
    if (!updateState.url) {
      return;
    }

    try {
      await Linking.openURL(updateState.url);
    } catch {
      // Ignore linking errors and keep the update modal visible.
    }
  };

  const handleSkipUpdate = () => {
    setUpdateState({
      visible: false,
      version: "",
      url: "",
    });
  };

  const handleRetryVersionCheck = async () => {
    setErrorState({
      visible: false,
      title: "",
      description: "",
    });
    setIsVersionCheckComplete(false);
    await checkForAppUpdate();
  };

  const handleCancelError = () => {
    setErrorState({
      visible: false,
      title: "",
      description: "",
    });

    if (Platform.OS === "android") {
      BackHandler.exitApp();
    }
  };

  useEffect(() => {
    checkInternet();
    checkForAppUpdate();

    UserDeviceService.initFcm().catch((error) => {
      console.log("FCM initialization failed:", error);
    });

    const unsubscribe = NetInfo.addEventListener((state) => {
      setIsConnected(state.isConnected);
    });

    const appStateSubscription = AppState.addEventListener(
      "change",
      (nextAppState: AppStateStatus) => {
        if (nextAppState === "active") {
          checkInternet();
          checkForAppUpdate();
        }
      }
    );

    return () => {
      unsubscribe();
      appStateSubscription.remove();
    };
  }, [checkForAppUpdate]);

  // Show No Internet screen
  if (isConnected === false) {
    return <NoInternetScreen onRetry={checkInternet} />;
  }

  // Initial loading
  if (isConnected === null) {
    return null;
  }

  return (
    <SafeAreaProvider>
      <AuthProvider>
        {!isVersionCheckComplete ? (
          <View style={styles.loaderContainer}>
            <ActivityIndicator size="large" color="#7A1F1F" />
          </View>
        ) : (
          <AppNavigator />
        )}

        {/* Global Loader */}
        <AppLoader />

        {/* Flash Messages */}
        <ToastHost />

        <Modal transparent animationType="fade" visible={updateState.visible}>
          <View style={styles.modalBackdrop}>
            <BlurView
              style={StyleSheet.absoluteFillObject}
              blurType="dark"
              blurAmount={8}
              reducedTransparencyFallbackColor="rgba(26, 14, 10, 0.72)"
            />
            <View style={styles.backdropTint} />
            <View style={styles.modalCard}>
              <Text style={styles.modalTitle}>Update Available</Text>
              <Text style={styles.modalMessage}>
                A new version of Gaur Prem Aashray is available. Please update the app for the
                latest features and a smoother devotional experience.
              </Text>
              <View style={styles.actionRow}>
                <Pressable style={styles.secondaryButton} onPress={handleSkipUpdate}>
                  <Text style={styles.secondaryButtonText}>Skip</Text>
                </Pressable>
                <Pressable style={styles.updateButton} onPress={handleUpdatePress}>
                  <Text style={styles.updateButtonText}>Update App</Text>
                </Pressable>
              </View>
            </View>
          </View>
        </Modal>

        <Modal transparent animationType="fade" visible={errorState.visible}>
          <View style={styles.modalBackdrop}>
            <BlurView
              style={StyleSheet.absoluteFillObject}
              blurType="dark"
              blurAmount={8}
              reducedTransparencyFallbackColor="rgba(26, 14, 10, 0.72)"
            />
            <View style={styles.backdropTint} />
            <View style={styles.modalCard}>
              <Text style={styles.modalTitle}>{errorState.title}</Text>
              <Text style={styles.modalMessage}>{errorState.description}</Text>
              <View style={styles.actionRow}>
                <Pressable style={styles.secondaryButton} onPress={handleCancelError}>
                  <Text style={styles.secondaryButtonText}>Cancel</Text>
                </Pressable>
                <Pressable style={styles.updateButton} onPress={handleRetryVersionCheck}>
                  <Text style={styles.updateButtonText}>Retry</Text>
                </Pressable>
              </View>
            </View>
          </View>
        </Modal>

      </AuthProvider>
    </SafeAreaProvider>
  );
}

const styles = StyleSheet.create({
  loaderContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#FFF8F0",
  },
  modalBackdrop: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  backdropTint: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(26, 14, 10, 0.38)",
  },
  modalCard: {
    width: "100%",
    maxWidth: 360,
    borderRadius: 20,
    paddingHorizontal: 24,
    paddingVertical: 28,
    backgroundColor: "#FFFDF8",
    alignItems: "center",
  },
  modalTitle: {
    fontSize: 22,
    fontWeight: "700",
    color: "#4A1C12",
    marginBottom: 12,
  },
  modalMessage: {
    fontSize: 15,
    lineHeight: 22,
    color: "#6A4A3C",
    textAlign: "center",
    marginBottom: 24,
  },
  updateButton: {
    minWidth: 160,
    borderRadius: 999,
    backgroundColor: "#C75B12",
    paddingHorizontal: 22,
    paddingVertical: 14,
  },
  actionRow: {
    width: "100%",
    flexDirection: "row",
    justifyContent: "center",
    gap: 12,
  },
  secondaryButton: {
    minWidth: 120,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: "#C75B12",
    backgroundColor: "#FFF7EF",
    paddingHorizontal: 22,
    paddingVertical: 14,
  },
  secondaryButtonText: {
    color: "#C75B12",
    fontSize: 16,
    fontWeight: "700",
    textAlign: "center",
  },
  updateButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "700",
    textAlign: "center",
  },
});
